


























































































































































import Vue from 'vue';
import { Component } from 'vue-property-decorator';

import { downloadByUrl } from '@/utils/file.util';
import { getObsToken } from '../../api/common/index';
import {
  CloudSpaceDraftItem,
  CloudSpaceDraftFetchResult,
  deleteCloudSpaceDraft,
  fetchCloudSpaceDraftByCode,
  getCloudSpaceDraftPage,
  saveCloudSpaceDraft,
} from '../../api/cloud-space/index';

type ObsTokenResult = {
  host: string;
  key: string;
  policy: string;
  signature: string;
  accessid: string;
  cdn: string;
};

@Component({
  components: {},
})
export default class CloudSpaceIndex extends Vue {
  acceptFileTypes = '.psd,.PSD,.ai,.AI,.tif,.TIF,.tiff,.TIFF,.pdf,.PDF';
  uploadServer = '';
  uploadPayload = {};
  uploading = false;
  loading = false;
  fetching = false;
  pageNum = 1;
  pageSize = 6;
  total = 0;
  accessCode = '';
  draftList: CloudSpaceDraftItem[] = [];
  foundDraft: (CloudSpaceDraftFetchResult & { accessCode?: string }) | null = null;

  async mounted() {
    await this.fetchDraftList();
  }

  async fetchDraftList(pageNum = this.pageNum) {
    this.loading = true;
    try {
      const res = await getCloudSpaceDraftPage({
        pageNum,
        pageSize: this.pageSize,
      });
      this.pageNum = pageNum;
      this.total = Number((res && res.total) || 0);
      this.draftList = Array.isArray(res && res.records) ? (res as any).records : [];
    } catch (error) {
      this.total = 0;
      this.draftList = [];
    } finally {
      this.loading = false;
    }
  }

  async handleBeforeUpload(file: any) {
    if (!this.isAllowedFileType(file.name)) {
      this.$message.warning('仅支持 PSD、AI、TIFF、PDF 格式');
      return false;
    }

    const suffixList = String(file.name || '').split('.');
    const suffix = suffixList[suffixList.length - 1] || '';

    try {
      this.uploading = true;
      const res = (await getObsToken({ suffix })) as ObsTokenResult;
      if (!res || !res.host || !res.key) {
        throw new Error('obs token invalid');
      }
      this.uploadServer = res.host;
      this.uploadPayload = {
        key: res.key,
        policy: res.policy,
        signature: res.signature,
        AccessKeyId: res.accessid,
        success_action_status: '200',
      };
      file.originalUrl = res.cdn + res.key;
      file.originalName = file.name;
    } catch (error) {
      this.resetUploadState();
      this.$message.error('上传凭证获取失败，请稍后重试');
      return false;
    }
    return true;
  }

  async handleUploadSuccess(response: any, file: any) {
    try {
      await saveCloudSpaceDraft({
        name: file.raw.originalName || file.name,
        url: file.raw.originalUrl,
        size: Number(file.size || (file.raw && file.raw.size) || 0),
        fileType: this.getFileExtension(file.name),
      });
      this.$message.success('上传成功');
      await this.fetchDraftList(1);
    } catch (error) {
      this.$message.error('设计稿保存失败，请稍后重试');
    } finally {
      this.resetUploadState();
    }
  }

  handleUploadError() {
    this.resetUploadState();
    this.$message.error('文件上传失败，请稍后重试');
  }

  async handleDelete(item: CloudSpaceDraftItem) {
    try {
      await this.$confirm(`确认删除设计稿“${item.name}”吗？`, '提示', {
        type: 'warning',
      });
      await deleteCloudSpaceDraft({ sn: item.sn });
      this.$message.success('删除成功');
      const currentPage = this.draftList.length === 1 && this.pageNum > 1 ? this.pageNum - 1 : this.pageNum;
      await this.fetchDraftList(currentPage);
    } catch (error) {
      if (error !== 'cancel') {
        this.$message.error('删除失败，请稍后重试');
      }
    }
  }

  async handleFetchDraft() {
    const normalizedAccessCode = String(this.accessCode || '')
      .trim()
      .toUpperCase();
    if (!normalizedAccessCode) {
      this.$message.warning('请输入识别码');
      return;
    }

    this.fetching = true;
    try {
      const res = await fetchCloudSpaceDraftByCode({
        accessCode: normalizedAccessCode,
      });
      if (!res || !res.url) {
        throw new Error('draft not found');
      }
      this.accessCode = normalizedAccessCode;
      this.foundDraft = {
        ...res,
        accessCode: normalizedAccessCode,
      };
      this.$message.success('已找到对应设计稿');
    } catch (error) {
      this.foundDraft = null;
      this.$message.error('未找到对应设计稿，请核对识别码');
    } finally {
      this.fetching = false;
    }
  }

  handleDownloadFoundDraft() {
    if (!this.foundDraft) {
      return;
    }
    downloadByUrl(this.foundDraft.url, this.foundDraft.name);
    this.$message.success('已开始下载原图');
  }

  handlePreviewFoundDraft() {
    if (!this.foundDraft) {
      return;
    }
    window.open(this.foundDraft.url, '_blank');
  }

  async handlePageChange(page: number) {
    await this.fetchDraftList(page);
  }

  async handleCopyCode(code: string) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const input = document.createElement('input');
        input.value = code;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      this.$message.success('识别码已复制');
    } catch (error) {
      this.$message.error('复制失败，请手动记录识别码');
    }
  }

  getFileExtension(name: string) {
    const parts = String(name || '').split('.');
    return (parts[parts.length - 1] || '').toLowerCase();
  }

  isAllowedFileType(name: string) {
    return ['psd', 'ai', 'tif', 'tiff', 'pdf'].includes(this.getFileExtension(name));
  }

  getFileTypeLabel(fileType: string) {
    return String(fileType || 'file')
      .slice(0, 4)
      .toUpperCase();
  }

  getCoverStyle(item: CloudSpaceDraftItem) {
    const fileType = this.getFileExtension(item.name || item.fileType);
    if (['psd', 'ai'].includes(fileType)) {
      return { background: 'linear-gradient(135deg, #f7c76e 0%, #d97706 100%)' };
    }
    if (['tif', 'tiff'].includes(fileType)) {
      return { background: 'linear-gradient(135deg, #a7f3d0 0%, #10b981 100%)' };
    }
    if (fileType === 'pdf') {
      return { background: 'linear-gradient(135deg, #fca5a5 0%, #ef4444 100%)' };
    }
    return { background: 'linear-gradient(135deg, #bfdbfe 0%, #2563eb 100%)' };
  }

  formatSize(size: number) {
    const value = Number(size || 0);
    if (value < 1024 * 1024) {
      return `${(value / 1024).toFixed(1)} KB`;
    }
    if (value < 1024 * 1024 * 1024) {
      return `${(value / 1024 / 1024).toFixed(1)} MB`;
    }
    return `${(value / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }

  formatDate(dateText: string) {
    if (!dateText) {
      return '--';
    }
    const normalized = String(dateText).replace('T', ' ');
    return normalized.slice(0, 16);
  }

  resetUploadState() {
    this.uploading = false;
    this.uploadServer = '';
    this.uploadPayload = {};
  }
}
